<!--
 * @desc Main vue component containing the nav bar.
 *
 * @author Mike Hamilton <mike@mikehamilton.ca>
-->

<template>
  <v-app dark>
    <!-- Dialog components -->
    <dialog-legal />
    <dialog-help />
    <dialog-login />
    <dialog-watermap />
    <snackbar-saved />

    <!-- Data dialogs components -->
    <data-snowmaking-hydrants />
    <data-snowmaking-fanguns />
    <data-snowmaking-airwaterguns />
    <data-snowmaking-pipeleaks />
    <data-snowmaking-potable />
    <data-snowmaking-images />
    <data-liftmaintenance-liftcables />
    <data-electrical-hv25kv />
    <data-electrical-substations />
    <data-electrical-secondary />
    <data-electrical-circuits />
    <data-electrical-splices />
    <data-patrol-permanentposts />
    <data-patrol-landmarks />
    <data-patrol-firstaid />
    <data-patrol-helivac />
    <data-bikepark-signs />
    <data-bikepark-trails />
    <data-bikepark-bridges />
    <data-signshop-signage />
    <data-wintertrails-landmarks />
    <data-wintertrails-skiruns />
    <data-wintertrails-skirunpolygons />
    <data-buildingmaintenance-buildings />

    <data-designatedroutes-snowmaking />
    <data-designatedroutes-standard />
    <data-designatedroutes-maintenance />
    <data-designatedroutes-patrol />
    <data-designatedroutes-rubbertiresnowmaking />
    <data-designatedroutes-rubbertirebikepark />
    <data-designatedroutes-rubbertireliftmaintenance />
    <data-designatedroutes-rubbertireelectricalmaintenance />
    <data-designatedroutes-rubbertirepatrol />
    <data-designatedroutes-rubbertirestandard />

    <data-hiking-trails />
    <data-liftmaintenance-liftcables />
    <data-liftmaintenance-lifttowers />
    <data-grooming-winch />
    <data-utilities-commlinescopper />
    <data-utilities-commlinesfiber />
    <data-summerops-fireboxes />

    <!-- Single dialog components -->
    <single-snowmaking-hydrants />
    <single-snowmaking-airwaterguns />
    <single-snowmaking-pipelines />
    <single-snowmaking-fanguns />
    <single-snowmaking-pipeleaks />
    <single-snowmaking-potable />
    <single-snowmaking-images />
    <single-liftmaintenance-liftcables />
    <single-liftmaintenance-lifttowers />
    <single-utilities-sewergaslines />
    <single-utilities-sewergaspoints />
    <single-utilities-commlinescopper />
    <single-utilities-commlinesfiber />
    <single-bikepark-trails />
    <single-bikepark-ehspoints />
    <single-bikepark-signs />
    <single-bikepark-bridges />
    <single-hiking-trails />
    <single-electrical-hv25kv />
    <single-electrical-substations />
    <single-electrical-secondary />
    <single-electrical-circuits />
    <single-electrical-splices />
    <single-patrol-permanentposts />
    <single-patrol-firstaid />
    <single-patrol-helivac />
    <single-patrol-landmarks />
    <single-signshop-signage />
    <single-designatedroutes-standard />
    <single-designatedroutes-snowmaking />
    <single-designatedroutes-maintenance />
    <single-designatedroutes-patrol />
    <single-designatedroutes-rubbertirebikepark />
    <single-designatedroutes-rubbertireelectricalmaintenance />
    <single-designatedroutes-rubbertireliftmaintenance />
    <single-designatedroutes-rubbertirepatrol />
    <single-designatedroutes-rubbertiresnowmaking />
    <single-designatedroutes-rubbertirestandard />
    <single-grooming-winch />
    <single-wintertrails-skiruns />
    <single-wintertrails-landmarks />
    <single-wintertrails-skirunpolygons />
    <single-liftopsdesignatedroutes-blackcombgondola />
    <single-liftopsdesignatedroutes-excelerator />
    <single-liftopsdesignatedroutes-excalibur />
    <single-liftopsdesignatedroutes-glacierexpress />
    <single-liftopsdesignatedroutes-jerseycream />
    <single-liftopsdesignatedroutes-crystal />
    <single-liftopsdesignatedroutes-seventhheaven />
    <single-liftopsdesignatedroutes-catskinner />
    <single-liftopsdesignatedroutes-magicchair />
    <single-liftopsdesignatedroutes-whistlercarpet />
    <single-liftopsdesignatedroutes-symphony />
    <single-liftopsdesignatedroutes-franz />
    <single-liftopsdesignatedroutes-harmony />
    <single-liftopsdesignatedroutes-peakchair />
    <single-liftopsdesignatedroutes-emerald />
    <single-liftopsdesignatedroutes-olympic />
    <single-liftopsdesignatedroutes-whistlertbar />
    <single-liftopsdesignatedroutes-redchair />
    <single-liftopsdesignatedroutes-garbanzo />
    <single-liftopsdesignatedroutes-creeksidegondola />
    <single-liftopsdesignatedroutes-blackcombcarpet />
    <single-liftopsdesignatedroutes-fitzsimmons />
    <single-summerops-fireboxes />

    <!-- Unauthorized -->
    <dialog-unauthorized />

    <!-- Password reset dialog -->
    <v-dialog dark v-model="passwordReset" max-width="500px">
      <v-card>
        <v-card-title>
          <span>
            Please check your email. As a security precaution, you must change
            your password via the link sent to your email just now.
          </span>
        </v-card-title>
        <v-card-actions>
          <v-btn color="success" flat @click.stop="passwordReset = false"
            >Ok</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Search location dialog -->
    <v-dialog dark v-model="searchLocation.dialog" max-width="500px">
      <v-card>
        <v-card-title>
          <span>Go to coordinates</span>
        </v-card-title>
        <v-card-text>
          <v-form v-model="valid">
            <v-text-field
              v-model="searchLocation.lat"
              prefix="latitude: "
              placeholder="e.g. 50.1xxxx"
              :rules="[rules.isNotEmpty, rules.isNumeric]"
            />
            <v-text-field
              v-model="searchLocation.lng"
              prefix="longitude: "
              placeholder="e.g. -122.9xxxx"
              :rules="[rules.isNotEmpty, rules.isNumeric]"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn
            color="primary"
            flat
            :disabled="!valid"
            @click.stop="
              zoomTo({
                center: computedLngLat,
                level: 18
              });
              drawPoint({
                center: computedLngLat
              });
              searchLocation.dialog = false;
              searchLocation.lat = null;
              searchLocation.lng = null;
            "
            >Go to</v-btn
          >
          <v-btn
            flat
            @click.stop="
              searchLocation.dialog = false;
              searchLocation.lat = null;
              searchLocation.lng = null;
            "
            >Cancel</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Tool bar -->
    <v-toolbar app clipped-left>
      <v-toolbar-side-icon
        @click.stop="drawer = !drawer"
        aria-label="navigation"
      ></v-toolbar-side-icon>
      <v-toolbar-title>{{ this.computedTitle }}</v-toolbar-title>
      <v-spacer></v-spacer>

      <!-- Water map button -->
      <v-btn color="primary" @click.stop="methodDialogUi('watermap')">
        Watermap
      </v-btn>
      <!-- Three dots menu -->
      <v-menu bottom left>
        <v-btn slot="activator" icon>
          <v-icon>more_vert</v-icon>
        </v-btn>
        <v-list>
          <v-list-tile @click.stop="searchLocation.dialog = true">
            <v-list-tile-title
              >Search
              Coordinates&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</v-list-tile-title
            >
          </v-list-tile>
          <v-list-tile @click.stop="methodDialogUi('help')">
            <v-list-tile-title
              >Help &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</v-list-tile-title
            >
          </v-list-tile>
          <v-list-tile @click="methodDialogUi('legal')">
            <v-list-tile-title>About&nbsp;&nbsp;&nbsp;&nbsp;</v-list-tile-title>
          </v-list-tile>
        </v-list>
      </v-menu>
    </v-toolbar>

    <!-- Side bar -->
    <v-navigation-drawer
      v-model="drawer"
      enable-resize-watcher
      app
      clipped
      color="secondary"
    >
      <v-list dense style="padding-top: 0px">
        <v-list-tile style="background-color: #26292e">
          <v-list-tile-content>
            <v-list-tile-title>
              <v-icon style="padding-right: 10px">account_box</v-icon>
              {{ computedAuthEmail }}
            </v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
        <v-list-tile style="background-color: #26292e">
          <v-list-tile-content style="padding-bottom: 10px">
            <div>
              <v-btn
                style="margin: 2px"
                small
                color="info"
                @click.stop="
                  logout();
                  drawer = false;
                "
                >Log out</v-btn
              >
              <v-btn small color="info" @click.stop="changePassword()"
                >Change Password</v-btn
              >
            </div>
          </v-list-tile-content>
        </v-list-tile>
        <!-- Quick Views -->
        <v-treeview :items="quickView" open-on-click>
          <template v-slot:label="{ item }">
            <span v-if="!item.children && item.layers">
              <v-btn
                color="info"
                small
                @click.stop="
                  item.layers.map(item =>
                    methodSetMapLayerVisibility(item.dept, item.layer, true)
                  )
                "
                >{{ item.name }}</v-btn
              >
            </span>
            <span v-else>{{ item.name }}</span>
          </template>
          <template v-slot:prepend="{ item }">
            <v-icon v-if="item.children">speed</v-icon>
          </template>
        </v-treeview>
        <!-- AERIAL IMAGERY-->
        <v-treeview :items="computedTreeviewImagery" open-on-click hoverable>
          <template v-slot:label="{ item }">
            <span
              v-if="!item.children"
              style="font-size: 15px"
              @click.stop="methodToggleImagryLayer(item.layer)"
              >{{ item.name }}</span
            >
            <span v-else style="font-size: 15px">{{ item.name }}</span>
          </template>
          <template v-slot:prepend="{ item }">
            <v-icon v-if="item.children">satellite</v-icon>
            <v-checkbox
              v-if="!item.children"
              v-model="item.visibility"
              color="blue"
              height="0"
              @click.stop="methodToggleImagryLayer(item.layer)"
            ></v-checkbox>
          </template>
        </v-treeview>
        <!-- BASEMAP-->
        <v-treeview :items="computedTreeviewBasemap" open-on-click hoverable>
          <template v-slot:label="{ item }">
            <span
              v-if="!item.children"
              style="font-size: 15px"
              @click.stop="methodToggleBasemapLayer(item.layer)"
              >{{ item.name }}</span
            >
            <span v-else style="font-size: 15px">{{ item.name }}</span>
          </template>
          <template v-slot:prepend="{ item }">
            <v-icon v-if="item.children">map</v-icon>
            <v-checkbox
              v-if="!item.children"
              v-model="item.visibility"
              color="blue"
              height="0"
              @click.stop="methodToggleBasemapLayer(item.layer)"
            ></v-checkbox>
          </template>
          <template v-slot:append="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">
                  <v-btn
                    v-if="!item.children"
                    icon
                    @click.stop="methodShowAttribution(item.attribution)"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon>info</v-icon>
                  </v-btn></span
                >
              </template>
              <span>Info</span>
            </v-tooltip>
          </template>
        </v-treeview>
        <!-- MAP LAYERS-->
        <v-treeview :items="computedTreeviewMapLayers" hoverable open-on-click>
          <!-- PREPEND (checkbox, icon) -->
          <template v-slot:prepend="{ item }">
            <v-checkbox
              v-if="item.children && !item.legend && !item.icon && !item.split"
              v-model="item.visibility"
              color="blue"
              height="0"
              style="padding-top: 8px"
              @click.stop="methodToggleMapLayer(item.dept, item.layer)"
            ></v-checkbox>
            <v-icon v-if="item.icon">{{ item.icon }}</v-icon>
            <v-img v-if="item.iconurl" :src="item.iconurl" max-width="20" />
          </template>
          <!-- LABEL -->
          <template v-slot:label="{ item }">
            <span style="font-size: 14px">
              {{ item.name }}
            </span>
          </template>
          <!-- APPEND (list view/KML icon) -->
          <template v-slot:append="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">
                  <v-btn
                    icon
                    v-if="item.hasListView"
                    @click.stop="methodToggleListLayer(item.dept, item.layer)"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon>list</v-icon>
                  </v-btn>
                </span>
              </template>
              <span>List view</span>
            </v-tooltip>
            <v-tooltip
              v-if="
                computedAuthDept === 'admin' ||
                  computedAuthProfile === 'maintenance'
              "
              bottom
            >
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">
                  <v-btn
                    icon
                    v-if="item.hasKML"
                    @click.stop="
                      methodHandleKML({
                        dept: item.dept,
                        layer: item.layer,
                        colKML: item.colKML,
                        fileName: `${item.dept}_${item.layer}.kml`
                      })
                    "
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon>download</v-icon>
                  </v-btn>
                </span>
              </template>
              <span>Download KML</span>
            </v-tooltip>
          </template>
        </v-treeview>
      </v-list>
    </v-navigation-drawer>

    <router-view />
    <!-- serviceWorker updated snackbar -->
    <v-snackbar
      v-model="snackWithButtons"
      :timeout="timeout"
      bottom
      left
      class="snack"
    >
      {{ snackWithBtnText }}
      <v-spacer />
      <v-btn dark flat color="#00f500" @click.native="refreshApp">
        {{ snackBtnText }}
      </v-btn>
      <v-btn icon @click="snackWithButtons = false">
        <v-icon>close</v-icon>
      </v-btn>
    </v-snackbar>
  </v-app>
</template>

<style>
.mapboxgl-popup-content {
  background-color: #404448 !important;
}

.v-list-fix {
  /* add spacing back to three dots menu */
  margin-right: 16px;
}
.v-input {
  flex: none;
}

/* multi line treeview labels */
.v-treeview-node__content,
.v-treeview-node__label {
  flex-shrink: 1;
}
.v-treeview-node__root {
  height: auto;
}
</style>

<script>
import auth0 from "auth0-js";
import dialogLegal from "./components/scoped/dialogLegal";
import dialogHelp from "./components/scoped/dialogHelp";
import dialogLogin from "./components/scoped/dialogLogin";
import dialogWatermap from "./components/scoped/dialogWatermap";
import dataSnowmakingHydrants from "./components/scoped/dataSnowmakingHydrants";
import dataSnowmakingFanguns from "./components/scoped/dataSnowmakingFanguns";
import dataSnowmakingAirwaterguns from "./components/scoped/dataSnowmakingAirwaterguns";
import dataSnowmakingPipeleaks from "./components/scoped/dataSnowmakingPipeleaks";
import dataSnowmakingImages from "./components/scoped/dataSnowmakingImages";
import dataSnowmakingPotable from "./components/scoped/dataSnowmakingPotable";
import dataElectricalHv25kv from "./components/scoped/dataElectricalHv25kv";
import dataElectricalSecondary from "./components/scoped/dataElectricalSecondary";
import dataElectricalSubstations from "./components/scoped/dataElectricalSubstations";
import dataElectricalCircuits from "./components/scoped/dataElectricalCircuits";
import dataElectricalSplices from "./components/scoped/dataElectricalSplices";
import dataLiftmaintenanceLiftcables from "./components/scoped/dataLiftmaintenanceLiftcables";
import dataLiftmaintenanceLifttowers from "./components/scoped/dataLiftmaintenanceLifttowers";
import dataPatrolPermanentposts from "./components/scoped/dataPatrolPermanentposts";
import dataPatrolLandmarks from "./components/scoped/dataPatrolLandmarks";
import dataPatrolFirstaid from "./components/scoped/dataPatrolFirstaid";
import dataPatrolHelivac from "./components/scoped/dataPatrolHelivac";
import dataBikeparkSigns from "./components/scoped/dataBikeparkSigns";
import dataBikeparkBridges from "./components/scoped/dataBikeparkBridges";
import dataDesignatedroutesSnowmaking from "./components/scoped/dataDesignatedroutesSnowmaking";
import dataDesignatedroutesStandard from "./components/scoped/dataDesignatedroutesStandard";
import dataDesignatedroutesMaintenance from "./components/scoped/dataDesignatedroutesMaintenance";
import dataDesignatedroutesPatrol from "./components/scoped/dataDesignatedroutesPatrol";
import dataDesignatedroutesRubbertiresnowmaking from "./components/scoped/dataDesignatedroutesRubbertiresnowmaking";
import dataDesignatedroutesRubbertirebikepark from "./components/scoped/dataDesignatedroutesRubbertirebikepark";
import dataDesignatedroutesRubbertireliftmaintenance from "./components/scoped/dataDesignatedroutesRubbertireliftmaintenance";
import dataDesignatedroutesRubbertireelectricalmaintenance from "./components/scoped/dataDesignatedroutesRubbertireelectricalmaintenance";
import dataDesignatedroutesRubbertirepatrol from "./components/scoped/dataDesignatedroutesRubbertirepatrol";
import dataDesignatedroutesRubbertirestandard from "./components/scoped/dataDesignatedroutesRubbertirestandard";
import dataBikeparkTrails from "./components/scoped/dataBikeparkTrails";
import dataSignshopSignage from "./components/scoped/dataSignshopSignage";
import dataWintertrailsLandmarks from "./components/scoped/dataWintertrailsLandmarks";
import dataWintertrailsSkiruns from "./components/scoped/dataWintertrailsSkiruns";
import dataWintertrailsSkirunpolygons from "./components/scoped/dataWintertrailsSkirunpolygons";
import dataHikingTrails from "./components/scoped/dataHikingTrails";
import dataBuildingmaintenanceBuildings from "./components/scoped/dataBuildingmaintenanceBuildings";
import dataGroomingWinch from "./components/scoped/dataGroomingWinch";
import dataUtilitiesCommlinescopper from "./components/scoped/dataUtilitiesCommlinescopper";
import dataUtilitiesCommlinesfiber from "./components/scoped/dataUtilitiesCommlinesfiber";
import dataSummeropsFireboxes from "./components/scoped/dataSummeropsFireboxes";

import singleSnowmakingHydrants from "./components/scoped/singleSnowmakingHydrants";
import singleSnowmakingAirwaterguns from "./components/scoped/singleSnowmakingAirwaterguns";
import singleSnowmakingPipelines from "./components/scoped/singleSnowmakingPipelines";
import singleSnowmakingFanguns from "./components/scoped/singleSnowmakingFanguns";
import singleSnowmakingPipeleaks from "./components/scoped/singleSnowmakingPipeleaks";
import singleSnowmakingPotable from "./components/scoped/singleSnowmakingPotable";
import singleSnowmakingImages from "./components/scoped/singleSnowmakingImages";
import singleLiftmaintenanceLiftcables from "./components/scoped/singleLiftmaintenanceLiftcables";
import singleLiftmaintenanceLifttowers from "./components/scoped/singleLiftmaintenanceLifttowers";
import singleUtilitiesSewergaslines from "./components/scoped/singleUtilitiesSewergaslines";
import singleUtilitiesSewergaspoints from "./components/scoped/singleUtilitiesSewergaspoints";
import singleUtilitiesCommlinescopper from "./components/scoped/singleUtilitiesCommlinescopper";
import singleUtilitiesCommlinesfiber from "./components/scoped/singleUtilitiesCommlinesfiber";
import singleBikeparkTrails from "./components/scoped/singleBikeparkTrails";
import singleBikeparkEhspoints from "./components/scoped/singleBikeparkEhspoints";
import singleBikeparkSigns from "./components/scoped/singleBikeparkSigns";
import singleBikeparkBridges from "./components/scoped/singleBikeparkBridges";
import singleHikingTrails from "./components/scoped/singleHikingTrails";
import singleElectricalHv25kv from "./components/scoped/singleElectricalHv25kv";
import singleElectricalSubstations from "./components/scoped/singleElectricalSubstations";
import singleElectricalSecondary from "./components/scoped/singleElectricalSecondary";
import singleElectricalCircuits from "./components/scoped/singleElectricalCircuits";
import singleElectricalSplices from "./components/scoped/singleElectricalSplices";
import singlePatrolPermanentposts from "./components/scoped/singlePatrolPermanentposts";
import singlePatrolFirstaid from "./components/scoped/singlePatrolFirstaid";
import singlePatrolHelivac from "./components/scoped/singlePatrolHelivac";
import singlePatrolLandmarks from "./components/scoped/singlePatrolLandmarks";
import singleSignshopSignage from "./components/scoped/singleSignshopSignage";
import singleDesignatedroutesStandard from "./components/scoped/singleDesignatedroutesStandard";
import singleDesignatedroutesSnowmaking from "./components/scoped/singleDesignatedroutesSnowmaking";
import singleDesignatedroutesMaintenance from "./components/scoped/singleDesignatedroutesMaintenance";
import singleDesignatedroutesPatrol from "./components/scoped/singleDesignatedroutesPatrol";
import singleDesigatedroutesRubbertirebikepark from "./components/scoped/singleDesignatedroutesRubbertirebikepark";
import singleDesigatedroutesRubbertireelectricalmaintenance from "./components/scoped/singleDesignatedroutesRubbertireelectricalmaintenance";
import singleDesigatedroutesRubbertireliftmaintenance from "./components/scoped/singleDesignatedroutesRubbertireliftmaintenance";
import singleDesigatedroutesRubbertirepatrol from "./components/scoped/singleDesignatedroutesRubbertirepatrol";
import singleDesigatedroutesRubbertiresnowmaking from "./components/scoped/singleDesignatedroutesRubbertiresnowmaking";
import singleDesigatedroutesRubbertirestandard from "./components/scoped/singleDesignatedroutesRubbertirestandard";
import singleGroomingWinch from "./components/scoped/singleGroomingWinch";
import singleWintertrailsSkiruns from "./components/scoped/singleWintertrailsSkiruns";
import singleWintertrailsLandmarks from "./components/scoped/singleWintertrailsLandmarks";
import singleLiftopsdesignatedroutesBlackcombgondola from "./components/scoped/singleLiftopsdesignatedroutesBlackcombgondola";
import singleLiftopsdesignatedroutesExcelerator from "./components/scoped/singleLiftopsdesignatedroutesExcelerator";
import singleLiftopsdesignatedroutesExcalibur from "./components/scoped/singleLiftopsdesignatedroutesExcalibur";
import singleLiftopsdesignatedroutesGlacierexpress from "./components/scoped/singleLiftopsdesignatedroutesGlacierexpress";
import singleLiftopsdesignatedroutesJerseycream from "./components/scoped/singleLiftopsdesignatedroutesJerseycream";
import singleLiftopsdesignatedroutesCrystal from "./components/scoped/singleLiftopsdesignatedroutesCrystal";
import singleLiftopsdesignatedroutesSeventhheaven from "./components/scoped/singleLiftopsdesignatedroutesSeventhheaven";
import singleLiftopsdesignatedroutesCatskinner from "./components/scoped/singleLiftopsdesignatedroutesCatskinner";
import singleLiftopsdesignatedroutesMagicchair from "./components/scoped/singleLiftopsdesignatedroutesMagicchair";
import singleLiftopsdesignatedroutesWhistlercarpet from "./components/scoped/singleLiftopsdesignatedroutesWhistlercarpet";
import singleLiftopsdesignatedroutesSymphony from "./components/scoped/singleLiftopsdesignatedroutesSymphony";
import singleLiftopsdesignatedroutesFranz from "./components/scoped/singleLiftopsdesignatedroutesFranz";
import singleLiftopsdesignatedroutesHarmony from "./components/scoped/singleLiftopsdesignatedroutesHarmony";
import singleLiftopsdesignatedroutesPeakchair from "./components/scoped/singleLiftopsdesignatedroutesPeakchair";
import singleLiftopsdesignatedroutesEmerald from "./components/scoped/singleLiftopsdesignatedroutesEmerald";
import singleLiftopsdesignatedroutesOlympic from "./components/scoped/singleLiftopsdesignatedroutesOlympic";
import singleLiftopsdesignatedroutesWhistlertbar from "./components/scoped/singleLiftopsdesignatedroutesWhistlertbar";
import singleLiftopsdesignatedroutesRedchair from "./components/scoped/singleLiftopsdesignatedroutesRedchair";
import singleLiftopsdesignatedroutesGarbanzo from "./components/scoped/singleLiftopsdesignatedroutesGarbanzo";
import singleLiftopsdesignatedroutesCreeksidegondola from "./components/scoped/singleLiftopsdesignatedroutesCreeksidegondola";
import singleLiftopsdesignatedroutesBlackcombcarpet from "./components/scoped/singleLiftopsdesignatedroutesBlackcombcarpet";
import singleLiftopsdesignatedroutesFitzsimmons from "./components/scoped/singleLiftopsdesignatedroutesFitzsimmons";
import singleSummeropsFireboxes from "./components/scoped/singleSummeropsFireboxes";
import singleWintertrailsSkirunpolygons from "./components/scoped/singleWintertrailsSkirunpolygons.vue";

import unauthorized from "./components/scoped/unauthorized";
import snackbarSaved from "./components/scoped/snackbarSaved";

export default {
  name: "App",
  components: {
    "dialog-legal": dialogLegal,
    "dialog-help": dialogHelp,
    "dialog-login": dialogLogin,
    "dialog-watermap": dialogWatermap,
    "data-snowmaking-hydrants": dataSnowmakingHydrants,
    "data-snowmaking-fanguns": dataSnowmakingFanguns,
    "data-snowmaking-airwaterguns": dataSnowmakingAirwaterguns,
    "data-snowmaking-pipeleaks": dataSnowmakingPipeleaks,
    "data-snowmaking-images": dataSnowmakingImages,
    "data-snowmaking-potable": dataSnowmakingPotable,
    "data-electrical-hv25kv": dataElectricalHv25kv,
    "data-electrical-substations": dataElectricalSubstations,
    "data-electrical-secondary": dataElectricalSecondary,
    "data-electrical-circuits": dataElectricalCircuits,
    "data-electrical-splices": dataElectricalSplices,
    "data-liftmaintenance-liftcables": dataLiftmaintenanceLiftcables,
    "data-liftmaintenance-lifttowers": dataLiftmaintenanceLifttowers,
    "data-patrol-permanentposts": dataPatrolPermanentposts,
    "data-patrol-landmarks": dataPatrolLandmarks,
    "data-patrol-firstaid": dataPatrolFirstaid,
    "data-patrol-helivac": dataPatrolHelivac,
    "data-bikepark-signs": dataBikeparkSigns,
    "data-bikepark-trails": dataBikeparkTrails,
    "data-signshop-signage": dataSignshopSignage,
    "data-bikepark-bridges": dataBikeparkBridges,
    "data-wintertrails-landmarks": dataWintertrailsLandmarks,
    "data-wintertrails-skiruns": dataWintertrailsSkiruns,
    "data-wintertrails-skirunpolygons": dataWintertrailsSkirunpolygons,
    "data-buildingmaintenance-buildings": dataBuildingmaintenanceBuildings,
    "data-designatedroutes-snowmaking": dataDesignatedroutesSnowmaking,
    "data-designatedroutes-standard": dataDesignatedroutesStandard,
    "data-designatedroutes-maintenance": dataDesignatedroutesMaintenance,
    "data-designatedroutes-patrol": dataDesignatedroutesPatrol,
    "data-designatedroutes-rubbertiresnowmaking": dataDesignatedroutesRubbertiresnowmaking,
    "data-designatedroutes-rubbertirebikepark": dataDesignatedroutesRubbertirebikepark,
    "data-designatedroutes-rubbertireliftmaintenance": dataDesignatedroutesRubbertireliftmaintenance,
    "data-designatedroutes-rubbertireelectricalmaintenance": dataDesignatedroutesRubbertireelectricalmaintenance,
    "data-designatedroutes-rubbertirepatrol": dataDesignatedroutesRubbertirepatrol,
    "data-designatedroutes-rubbertirestandard": dataDesignatedroutesRubbertirestandard,
    "data-hiking-trails": dataHikingTrails,
    "data-grooming-winch": dataGroomingWinch,
    "data-utilities-commlinescopper": dataUtilitiesCommlinescopper,
    "data-utilities-commlinesfiber": dataUtilitiesCommlinesfiber,
    "data-summerops-fireboxes": dataSummeropsFireboxes,
    "single-snowmaking-hydrants": singleSnowmakingHydrants,
    "single-snowmaking-pipelines": singleSnowmakingPipelines,
    "single-snowmaking-fanguns": singleSnowmakingFanguns,
    "single-snowmaking-airwaterguns": singleSnowmakingAirwaterguns,
    "single-snowmaking-pipeleaks": singleSnowmakingPipeleaks,
    "single-snowmaking-potable": singleSnowmakingPotable,
    "single-snowmaking-images": singleSnowmakingImages,
    "single-liftmaintenance-liftcables": singleLiftmaintenanceLiftcables,
    "single-liftmaintenance-lifttowers": singleLiftmaintenanceLifttowers,
    "single-utilities-sewergaslines": singleUtilitiesSewergaslines,
    "single-utilities-sewergaspoints": singleUtilitiesSewergaspoints,
    "single-utilities-commlinescopper": singleUtilitiesCommlinescopper,
    "single-utilities-commlinesfiber": singleUtilitiesCommlinesfiber,
    "single-bikepark-trails": singleBikeparkTrails,
    "single-bikepark-ehspoints": singleBikeparkEhspoints,
    "single-bikepark-signs": singleBikeparkSigns,
    "single-bikepark-bridges": singleBikeparkBridges,
    "single-hiking-trails": singleHikingTrails,
    "single-electrical-hv25kv": singleElectricalHv25kv,
    "single-electrical-substations": singleElectricalSubstations,
    "single-electrical-secondary": singleElectricalSecondary,
    "single-electrical-circuits": singleElectricalCircuits,
    "single-electrical-splices": singleElectricalSplices,
    "single-patrol-permanentposts": singlePatrolPermanentposts,
    "single-patrol-firstaid": singlePatrolFirstaid,
    "single-patrol-helivac": singlePatrolHelivac,
    "single-patrol-landmarks": singlePatrolLandmarks,
    "single-signshop-signage": singleSignshopSignage,
    "single-designatedroutes-standard": singleDesignatedroutesStandard,
    "single-designatedroutes-snowmaking": singleDesignatedroutesSnowmaking,
    "single-designatedroutes-maintenance": singleDesignatedroutesMaintenance,
    "single-designatedroutes-patrol": singleDesignatedroutesPatrol,
    "single-designatedroutes-rubbertirebikepark": singleDesigatedroutesRubbertirebikepark,
    "single-designatedroutes-rubbertireelectricalmaintenance": singleDesigatedroutesRubbertireelectricalmaintenance,
    "single-designatedroutes-rubbertireliftmaintenance": singleDesigatedroutesRubbertireliftmaintenance,
    "single-designatedroutes-rubbertirepatrol": singleDesigatedroutesRubbertirepatrol,
    "single-designatedroutes-rubbertiresnowmaking": singleDesigatedroutesRubbertiresnowmaking,
    "single-designatedroutes-rubbertirestandard": singleDesigatedroutesRubbertirestandard,
    "single-grooming-winch": singleGroomingWinch,
    "single-wintertrails-skiruns": singleWintertrailsSkiruns,
    "single-wintertrails-landmarks": singleWintertrailsLandmarks,
    "single-wintertrails-skirunpolygons": singleWintertrailsSkirunpolygons,
    "single-liftopsdesignatedroutes-blackcombgondola": singleLiftopsdesignatedroutesBlackcombgondola,
    "single-liftopsdesignatedroutes-excelerator": singleLiftopsdesignatedroutesExcelerator,
    "single-liftopsdesignatedroutes-excalibur": singleLiftopsdesignatedroutesExcalibur,
    "single-liftopsdesignatedroutes-glacierexpress": singleLiftopsdesignatedroutesGlacierexpress,
    "single-liftopsdesignatedroutes-jerseycream": singleLiftopsdesignatedroutesJerseycream,
    "single-liftopsdesignatedroutes-crystal": singleLiftopsdesignatedroutesCrystal,
    "single-liftopsdesignatedroutes-seventhheaven": singleLiftopsdesignatedroutesSeventhheaven,
    "single-liftopsdesignatedroutes-catskinner": singleLiftopsdesignatedroutesCatskinner,
    "single-liftopsdesignatedroutes-magicchair": singleLiftopsdesignatedroutesMagicchair,
    "single-liftopsdesignatedroutes-whistlercarpet": singleLiftopsdesignatedroutesWhistlercarpet,
    "single-liftopsdesignatedroutes-symphony": singleLiftopsdesignatedroutesSymphony,
    "single-liftopsdesignatedroutes-franz": singleLiftopsdesignatedroutesFranz,
    "single-liftopsdesignatedroutes-harmony": singleLiftopsdesignatedroutesHarmony,
    "single-liftopsdesignatedroutes-peakchair": singleLiftopsdesignatedroutesPeakchair,
    "single-liftopsdesignatedroutes-emerald": singleLiftopsdesignatedroutesEmerald,
    "single-liftopsdesignatedroutes-olympic": singleLiftopsdesignatedroutesOlympic,
    "single-liftopsdesignatedroutes-whistlertbar": singleLiftopsdesignatedroutesWhistlertbar,
    "single-liftopsdesignatedroutes-redchair": singleLiftopsdesignatedroutesRedchair,
    "single-liftopsdesignatedroutes-garbanzo": singleLiftopsdesignatedroutesGarbanzo,
    "single-liftopsdesignatedroutes-creeksidegondola": singleLiftopsdesignatedroutesCreeksidegondola,
    "single-liftopsdesignatedroutes-blackcombcarpet": singleLiftopsdesignatedroutesBlackcombcarpet,
    "single-liftopsdesignatedroutes-fitzsimmons": singleLiftopsdesignatedroutesFitzsimmons,
    "single-summerops-fireboxes": singleSummeropsFireboxes,

    "dialog-unauthorized": unauthorized,
    "snackbar-saved": snackbarSaved
  },
  data() {
    return {
      quickView: [
        {
          id: 0,
          name: "Quick Views",
          children: [
            {
              id: 0,
              name: "Underground Utilities",
              layers: [
                { dept: "utilities", layer: "commlinescopper" },
                { dept: "utilities", layer: "commlinesfiber" },
                { dept: "utilities", layer: "sewergaslines" },
                { dept: "utilities", layer: "sewergaspoints" },
                { dept: "electrical", layer: "hv25kv" },
                { dept: "electrical", layer: "secondary" },
                { dept: "snowmaking", layer: "pipelines" },
                { dept: "snowmaking", layer: "potable" }
              ]
            }
          ]
        }
      ],
      drawer: false,
      authenticated: false,
      passwordReset: false,
      refreshing: false,
      registration: null,
      snackBtnText: "",
      snackWithBtnText: "",
      snackWithButtons: false,
      timeout: 0,
      searchLocation: {
        dialog: false,
        lat: null,
        lng: null
      },
      rules: {
        isNotEmpty: value => !!value || "Can't be empty",
        isNumeric: value =>
          /^-{0,1}\d*\.{0,1}\d+$/.test(value) || "Only Numbers"
      },
      valid: false
    };
  },
  created() {
    // Listen for swUpdated event and display refresh snackbar as required.
    document.addEventListener("swUpdated", this.showRefreshUI, { once: true });
    // Refresh all open app tabs when a new service worker is installed.
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (this.refreshing) return;
      this.refreshing = true;
      window.location.reload();
    });
  },
  computed: {
    computedTreeviewMapLayers() {
      const arr = [];
      Object.entries(this.computedDeptData).map((item, i) => {
        // Push the dept titles
        arr.push({
          id: `item-${i}`,
          name: item[1].deptTitle,
          dept: item[1].name,
          icon: item[1].icon,
          // if dept is split, push the split names and then blank children to actually hold layers
          children: []
        });
        // if the layer is split, push in the splitNames and blank children
        if (item[1].split) {
          item[1].splitNames.map(sn =>
            arr[i].children.push({
              id: `item-child-${i}-${sn.name}`,
              name: sn.name,
              split: true,
              children: []
            })
          );
        }
        Object.entries(item[1].layers).map((childItem, iChild) => {
          if (childItem[1].layerTitle) {
            if (
              !childItem[1].showOnly ||
              childItem[1].showOnly === this.computedAuthDept ||
              this.computedAuthDept === "admin"
            )
              if (!item[1].split) {
                // Not split (department)
                arr[i].children.push({
                  id: `item-child-child-${iChild}-${name}`,
                  name: childItem[1].layerTitle,
                  layer: childItem[1].name,
                  visibility: childItem[1].visibility.mapView,
                  hasListView: childItem[1].hasListView,
                  hasKML: childItem[1].hasKML,
                  colKML: childItem[1].colKML,
                  dept: item[1].name,
                  children: childItem[1].legend
                    ? childItem[1].legend
                    : childItem[1].split
                    ? [
                        // split layers go here?
                      ]
                    : []
                });
              } else {
                // Split (department)
                arr[i].children[childItem[1].splitIndex].children.push({
                  id: `item-child-child-split-${iChild}-${name}`,
                  name: childItem[1].layerTitle,
                  layer: childItem[1].name,
                  visibility: childItem[1].visibility.mapView,
                  hasListView: childItem[1].hasListView,
                  hasKML: childItem[1].hasKML,
                  colKML: childItem[1].colKML,
                  dept: item[1].name,
                  children: childItem[1].legend ? childItem[1].legend : []
                });
              }
          }
        });
      });
      return arr;
    },
    computedTreeviewImagery() {
      const arr = [
        {
          id: 0,
          name: "Aerial Imagery",
          children: []
        }
      ];
      let i = 0;
      Object.keys(this.computedImagryData).map(key => {
        arr[0].children.push({
          id: `imagery-${i}`,
          name: this.computedImagryData[key].layerTitle,
          layer: this.computedImagryData[key].name,
          visibility: this.computedImagryData[key].visibility
        });
        i++;
      });
      return arr;
    },
    computedTreeviewBasemap() {
      const arr = [
        {
          id: 0,
          name: "Base Maps",
          children: []
        }
      ];
      let i = 0;
      Object.keys(this.computedBasemapData).map(key => {
        arr[0].children.push({
          id: `basemap-${i}`,
          name: this.computedBasemapData[key].layerTitle,
          layer: this.computedBasemapData[key].name,
          visibility: this.computedBasemapData[key].visibility,
          attribution: this.computedBasemapData[key].attribution
        });
        i++;
      });
      return arr;
    },
    // Returns the coordinates typed into the searchLocation dialog box
    computedLngLat() {
      if (
        this.searchLocation.lat === null ||
        this.searchLocation.lng === null
      ) {
        return [-122.9, 50.1];
      } else {
        if (parseFloat(this.searchLocation.lng) > 0) {
          return [
            parseFloat(this.searchLocation.lng * -1),
            parseFloat(this.searchLocation.lat)
          ];
        } else {
          return [
            parseFloat(this.searchLocation.lng),
            parseFloat(this.searchLocation.lat)
          ];
        }
      }
    },
    computedDeptData() {
      return this.$store.state.mapLayers;
    },
    computedImagryData() {
      return this.$store.state.imagryLayers;
    },
    computedBasemapData() {
      return this.$store.state.basemapLayers;
    },
    computedTitle() {
      return this.$store.state.general.title;
    },
    // SNOWMAKING
    computedSnowmakingFangunsMapView() {
      return this.$store.state.mapLayers.snowmaking.layers.fanguns.visibility
        .mapView;
    },
    computedSnowmakingFangunSingleView() {
      return this.$store.state.mapLayers.snowmaking.layers.fanguns.visibility
        .singleView;
    },
    computedSnowmakingAirwatergunsMapView() {
      return this.$store.state.mapLayers.snowmaking.layers.airwaterguns
        .visibility.mapView;
    },
    computedSnowmakingAirwatergunSingleView() {
      return this.$store.state.mapLayers.snowmaking.layers.airwaterguns
        .visibility.singleView;
    },
    computedSnowmakingHydrantsMapView() {
      return this.$store.state.mapLayers.snowmaking.layers.hydrants.visibility
        .mapView;
    },
    computedSnowmakingHydrantSingleView() {
      return this.$store.state.mapLayers.snowmaking.layers.hydrants.visibility
        .singleView;
    },
    computedSnowmakingPipelinesMapView() {
      return this.$store.state.mapLayers.snowmaking.layers.pipelines.visibility
        .mapView;
    },
    computedSnowmakingPotableSingleView() {
      return this.$store.state.mapLayers.snowmaking.layers.potable.visibility
        .singleView;
    },
    computedSnowmakingPotableMapView() {
      return this.$store.state.mapLayers.snowmaking.layers.potable.visibility
        .mapView;
    },
    computedSnowmakingPipelinesSingleView() {
      return this.$store.state.mapLayers.snowmaking.layers.pipelines.visibility
        .singleView;
    },
    computedSnowmakingPipeleaksMapView() {
      return this.$store.state.mapLayers.snowmaking.layers.pipeleaks.visibility
        .mapView;
    },
    computedSnowmakingPipeleaksSingleView() {
      return this.$store.state.mapLayers.snowmaking.layers.pipeleaks.visibility
        .singleView;
    },
    computedSnowmakingImagesMapView() {
      return this.$store.state.mapLayers.snowmaking.layers.images.visibility
        .mapView;
    },
    computedSnowmakingImagesSingleView() {
      return this.$store.state.mapLayers.snowmaking.layers.images.visibility
        .singleView;
    },
    computedBikeparkBridgesSingleView() {
      return this.$store.state.mapLayers.bikepark.layers.bridges.visibility
        .singleView;
    },
    // LIFT MAINTENANCE
    computedLiftmaintenanceLiftcablesMapView() {
      return this.$store.state.mapLayers.liftmaintenance.layers.liftcables
        .visibility.mapView;
    },
    computedLiftmaintenanceLiftcablesSingleView() {
      return this.$store.state.mapLayers.liftmaintenance.layers.liftcables
        .visibility.singleView;
    },
    // LAND USE
    computedLanduseCratenureMapView() {
      return this.$store.state.mapLayers.landuse.layers.cratenure.visibility
        .mapView;
    },
    // UTILITIES
    computedUtilitiesSewergaslinesMapView() {
      return this.$store.state.mapLayers.utilities.layers.sewergaslines
        .visibility.mapView;
    },
    computedUtilitiesSewergaspointsMapView() {
      return this.$store.state.mapLayers.utilities.layers.sewergaspoints
        .visibility.mapView;
    },
    computedUtilitiesCommlinesMapView() {
      return this.$store.state.mapLayers.utilities.layers.commlines.visibility
        .mapView;
    },
    // BIKE PARK
    computedBikeparkTrailsMapView() {
      return this.$store.state.mapLayers.bikepark.layers.trails.visibility
        .mapView;
    },
    computedBikeparkEhspointsMapView() {
      return this.$store.state.mapLayers.bikepark.layers.ehspoints.visibility
        .mapView;
    },
    computedBikeparkSignsMapView() {
      return this.$store.state.mapLayers.bikepark.layers.signs.visibility
        .mapView;
    },
    computedBikeparkBridgesMapView() {
      return this.$store.state.mapLayers.bikepark.layers.bridges.visibility
        .mapView;
    },
    // SUMMER HIKING
    computedHikingTrailsMapView() {
      return this.$store.state.mapLayers.hiking.layers.trails.visibility
        .mapView;
    },
    // ELECTRICAL
    computedElectricalHv25kvMapView() {
      return this.$store.state.mapLayers.electrical.layers.hv25kv.visibility
        .mapView;
    },
    computedElectricalHv25kvSingleView() {
      return this.$store.state.mapLayers.electrical.layers.hv25kv.visibility
        .singleView;
    },
    computedElectricalSecondarySingleView() {
      return this.$store.state.mapLayers.electrical.layers.secondary.visibility
        .singleView;
    },
    computedElectricalSubstationsMapView() {
      return this.$store.state.mapLayers.electrical.layers.substations
        .visibility.mapView;
    },
    computedElectricalSplicesSingleView() {
      return this.$store.state.mapLayers.electrical.layers.splices.visibility
        .singleView;
    },
    computedElectricalSplicesMapView() {
      return this.$store.state.mapLayers.electrical.layers.splices.visibility
        .mapView;
    },
    // PATROL
    computedPatrolPermanentpostsMapView() {
      return this.$store.state.mapLayers.patrol.layers.permanentposts.visibility
        .mapView;
    },
    computedPatrolHelivacMapView() {
      return this.$store.state.mapLayers.patrol.layers.helivac.visibility
        .mapView;
    },
    // SIGNSHOP
    computedSignshopSignageMapView() {
      return this.$store.state.mapLayers.signshop.layers.signage.visibility
        .mapView;
    },
    // GROOMING
    computedGroomingWinchMapView() {
      return this.$store.state.mapLayers.grooming.layers.winch.visibility
        .mapView;
    },
    // WINTER TRAILS
    computedWintertrailsSkirunsMapView() {
      return this.$store.state.mapLayers.wintertrails.layers.skiruns.visibility
        .mapView;
    },
    computedWintertrailsLandmarksMapView() {
      return this.$store.state.mapLayers.wintertrails.layers.landmarks
        .visibility.mapView;
    },
    computedWintertrailsSkirunpolygonsMapView() {
      return this.$store.state.mapLayers.wintertrails.layers.skirunpolygons
        .visibility.mapView;
    },
    // DESIGNATED ROUTES
    computedDesignatedroutesStandardMapView() {
      return this.$store.state.mapLayers.designatedroutes.layers.standard
        .visibility.mapView;
    },
    computedDesignatedroutesSnowmakingMapView() {
      return this.$store.state.mapLayers.designatedroutes.layers.snowmaking
        .visibility.mapView;
    },
    computedDesignatedroutesMaintenanceMapView() {
      return this.$store.state.mapLayers.designatedroutes.layers.maintenance
        .visibility.mapView;
    },
    computedDesignatedroutesParkingMapView() {
      return this.$store.state.mapLayers.designatedroutes.layers.parking
        .visibility.mapView;
    },
    computedDesignatedroutesPatrolMapView() {
      return this.$store.state.mapLayers.designatedroutes.layers.patrol
        .visibility.mapView;
    },
    computedLiftopsdesignatedroutesBlackcombgondolaMapView() {
      return this.$store.state.mapLayers.liftopsdesignatedroutes.layers
        .blackcombgondola.visibility.mapView;
    },
    computedSummeropsFireboxesMapView() {
      return this.$store.state.mapLayers.summerops.layers.fireboxes.visibility
        .mapView;
    },
    computedSummeropsCalloutsMapView() {
      return this.$store.state.mapLayers.summerops.layers.callouts.visibility
        .mapView;
    },
    computedAuthToken() {
      return this.$store.state.auth.token;
    },
    computedAuthEmail() {
      return this.$store.state.auth.profile.email;
    },
    computedAuthState() {
      return this.$store.state.auth.state;
    },
    computedGeneral() {
      return this.$store.state.general;
    }
  },
  methods: {
    methodHandleKML(payload) {
      this.$store
        .dispatch("actionGetGeoJson", {
          url: `https://api2.alchemymapping.com/vail/whistler/${payload.dept}/${payload.layer}`,
          dept: payload.dept,
          layer: payload.layer,
          token: this.computedAuthToken
        })
        .then(() => {
          this.methodDownloadKML(
            this.$store.state.mapLayers[payload.dept].layers[payload.layer]
              .geojson,
            payload.colKML,
            payload.fileName
          );
        });
    },
    showRefreshUI(e) {
      // Display a snackbar inviting the user to refresh/reload the app due
      // to an app update being available.
      // The new service worker is installed, but not yet active.
      // Store the ServiceWorkerRegistration instance for later use.
      this.registration = e.detail;
      this.snackBtnText = "Refresh";
      this.snackWithBtnText = `New version of ${this.computedGeneral.title} available!`;
      this.snackWithButtons = true;
    },
    refreshApp() {
      this.snackWithButtons = false;
      // Protect against missing registration.waiting.
      if (!this.registration || !this.registration.waiting) {
        return;
      }
      this.registration.waiting.postMessage("skipWaiting");
    },
    login() {
      this.webAuth.authorize();
    },
    changePassword() {
      this.webAuth.changePassword(
        {
          connection: "Username-Password-Authentication",
          email: this.computedAuthEmail
        },
        err => {
          if (err) {
            console.log(`err ${JSON.stringify(err)}`);
          } else {
            this.drawer = false;
            this.passwordReset = true;
          }
        }
      );
    },
    logout() {
      this.methodAuthToken(null);
      this.methodAuthState(false);
      this.authenticated = false;
    },
    methodAuthProfile(profile) {
      this.$store.commit("mutationAuthProfile", {
        profile
      });
    },
    methodAuthToken(token) {
      this.$store.commit("mutationAuthToken", {
        token
      });
    },
    methodAuthState(bool) {
      this.$store.commit("mutationAuthState", {
        bool
      });
    },
    methodDialogUi(dialog) {
      this.$store.commit("mutationDialogUi", {
        dialog
      });
    },
    methodShowAttribution(attr) {
      alert(attr);
    },
    methodToggleImagryLayer(layer) {
      this.$store.commit("mutationImagryLayer", {
        layer: layer
      });
    },
    methodToggleBasemapLayer(layer) {
      this.$store.commit("mutationBasemapLayer", {
        layer: layer
      });
    },
    methodToggleListLayer(dept, layer) {
      this.$store.commit("mutationListLayer", {
        dept: dept,
        layer: layer
      });
    },
    handleAuthentication() {
      let self = this;
      self.webAuth.parseHash(function(err, authResult) {
        if (authResult && authResult.accessToken && authResult.idToken) {
          self.methodAuthState(true);
          self.methodAuthToken(authResult.accessToken);
          self.methodAuthProfile(authResult.idTokenPayload);
        } else if (err) {
          console.log(err);
          alert(
            "Error: " + err.error + ". Check the console for further details."
          );
        }
      });
    }
  },
  beforeMount() {
    const clientID = "3qEYZAk8JEZqNfU619PLB8mghB1flDBB"; // Auth0 client id
    const domain = "snowmaking.auth0.com"; // Auth0 domain

    this.webAuth = new auth0.WebAuth({
      domain,
      clientID,
      responseType: "token id_token",
      audience: "GeoWBRS",
      scope: "openid email app_metadata",
      redirectUri: "https://gis.wbsnowmaking.com/callback"
      //redirectUri: "http://localhost:1234/callback"
    });

    this.handleAuthentication();
  },
  mounted() {
    // Remove vuex and geowb-v1 localstorage key since we are changing to "geowb-v3" key
    window.localStorage.removeItem("vuex");
    window.localStorage.removeItem("geowb-v1");
    window.localStorage.removeItem("geowb-v2");

    // Duhhhhrty hack, seems to work, though!
    // The page was redirecting before that authState could be set
    // So I set a 250ms timeout to allow for it to be set before
    setTimeout(() => {
      if (this.computedAuthState === false) this.login();
    }, 500);

    // Refresh  the token every 10 minutes.
    setInterval(() => {
      let self = this;
      if (this.computedAuthState === true) {
        this.webAuth.checkSession({}, function(err, authResult) {
          if (err) {
            console.log(err);
          } else {
            self.methodAuthState(true);
            self.methodAuthToken(authResult.accessToken);
            self.methodAuthProfile(authResult.idTokenPayload);
          }
        });
      }
    }, 600 * 1000); // refresh token every 600 seconds
  },
  watch: {
    "$store.state.auth.state": function(newValue) {
      if (newValue === false) {
        this.login();
      }
    }
  }
};
</script>
